import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon } from '../Icon';
import { useFocusTrap } from '../useFocusTrap';
import { useModalEscape } from '../ConfirmModal';

type ModalSize = 'sm' | 'md' | 'lg';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  size?: ModalSize;
  children: ReactNode;
  /**
   * Slot del footer (botones de acción). Si no se pasa, no se renderiza el
   * footer — el modal queda con solo header + body (útil para modales de
   * solo-lectura como detail viewers).
   */
  footer?: ReactNode;
  /**
   * Si true, el modal NO cierra al click en el backdrop. Útil para modales
   * con form sucio: queremos exigir cancelar/cerrar explícito para no
   * perder cambios silenciosamente. Default: false (cierra al backdrop).
   */
  dismissOnBackdrop?: boolean;
  /**
   * Si true, deshabilita el cierre via Escape + click backdrop. Útil para
   * estados busy/loading donde el cierre prematuro corrompería el state.
   */
  busy?: boolean;
  /**
   * Clase CSS extra para el contenedor `.modal`. NO se debe usar para
   * sobrescribir layout core — solo para tweaks específicos (max-width
   * custom, padding extra). Para usos comunes preferir `size`.
   */
  className?: string;
  /**
   * aria-label alternativo si `title` no es texto plano (ej. ReactNode con
   * iconos). El screen reader anuncia `aria-labelledby` por default — si
   * el title visual contiene markup, pasar este label.
   */
  ariaLabel?: string;
}

/**
 * Modal shell reusable. Wrappea las clases CSS legacy del módulo
 * (`.modal-overlay`, `.modal`, `.modal-head`, `.modal-body`, `.modal-foot`
 * en `tokens.css`) en un componente con API canónica para los refactors
 * MODAL-B/C/D/E/F.
 *
 * Decisiones:
 * - Reusa `useModalEscape` + `useFocusTrap` existentes. No reimplementa la
 *   stack de Escape para no bifurcar comportamiento con ConfirmModal /
 *   AlertModal.
 * - Tamaño via atributo `data-size` (sm/md/lg). CSS aplica max-width vía
 *   selector `.modal[data-size="..."]`.
 * - Body scroll lock cuando abierto: agrega `overflow: hidden` al
 *   `document.body` y lo restaura al cerrar. Compatible con múltiples
 *   modales (count en estado del módulo).
 * - Si `!open`, renderiza null — el consumer puede dejar `<Modal open={x}>`
 *   sin guard extra.
 */
const modalOpenCount = { value: 0 };

function lockBodyScroll() {
  if (typeof document === 'undefined') return;
  modalOpenCount.value += 1;
  if (modalOpenCount.value === 1) {
    document.body.dataset.modalScrollLock = 'true';
  }
}

function unlockBodyScroll() {
  if (typeof document === 'undefined') return;
  modalOpenCount.value = Math.max(0, modalOpenCount.value - 1);
  if (modalOpenCount.value === 0) {
    delete document.body.dataset.modalScrollLock;
  }
}

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  dismissOnBackdrop = true,
  busy = false,
  className,
  ariaLabel,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  useModalEscape(onClose, !open || busy);
  useFocusTrap(containerRef, open && !busy);

  useEffect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [open]);

  if (!open) return null;

  const classes = ['modal'];
  if (className) classes.push(className);

  const handleBackdropClick = () => {
    if (busy) return;
    if (!dismissOnBackdrop) return;
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div
        className={classes.join(' ')}
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel ? undefined : titleId}
        aria-label={ariaLabel}
        aria-describedby={description ? descriptionId : undefined}
        data-size={size}
      >
        <div className="modal-head">
          <div className="modal-head-titles">
            <h2 id={titleId}>{title}</h2>
            {description ? (
              <p id={descriptionId} className="modal-description">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            disabled={busy}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
